<template>
  <div v-if="actOnEnabled || shareEnabled" class="viq-actions">
    <button v-if="actOnEnabled" class="viq-actions__btn" @click="handleActOnThis">
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <path d="M5 7l2 2 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
        <rect x="1" y="1" width="12" height="12" rx="2" stroke="currentColor" stroke-width="1" />
      </svg>
      Act on this
    </button>
    <button v-if="shareEnabled" class="viq-actions__btn" @click="handleSaveShare">
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <path d="M4 8.5a2 2 0 100-4 2 2 0 000 4zM10 3.5a2 2 0 100-4 2 2 0 000 4zM10 13.5a2 2 0 100-4 2 2 0 000 4z" stroke="currentColor" stroke-width="1" />
        <path d="M5.7 7.4l2.6 1.7M5.7 5.6l2.6-1.7" stroke="currentColor" stroke-width="1" />
      </svg>
      Save &amp; share
    </button>

    <!-- Act on this modal -->
    <transition name="viq-fade">
      <div v-if="modalOpen" class="viq-actions-modal" @click.self="closeModal">
        <div class="viq-actions-modal__panel">
          <div class="viq-actions-modal__header">
            <div class="viq-actions-modal__header-left">
              <div class="viq-actions-modal__icon">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M6 8l2 2 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                  <rect x="1" y="1" width="14" height="14" rx="2" stroke="currentColor" stroke-width="1" />
                </svg>
              </div>
              <div>
                <h3 class="viq-actions-modal__title">Send to Workspace</h3>
                <p class="viq-actions-modal__subtitle">Create action items on your board</p>
              </div>
            </div>
            <button class="viq-actions-modal__close" @click="closeModal">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
              </svg>
            </button>
          </div>

          <div class="viq-actions-modal__body">
            <!-- Extracting -->
            <div v-if="extracting" class="viq-actions-modal__loading">
              <div class="viq-actions-modal__spinner" />
              <span>Extracting action items...</span>
            </div>

            <!-- Error -->
            <div v-else-if="modalError" class="viq-actions-modal__error">
              {{ modalError }}
            </div>

            <!-- Done -->
            <div v-else-if="done" class="viq-actions-modal__done">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M5 10l4 4 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              <span>{{ selectedCount }} action{{ selectedCount !== 1 ? 's' : '' }} sent to Workspace</span>
            </div>

            <!-- Empty -->
            <div v-else-if="!extracting && items.length === 0" class="viq-actions-modal__empty">
              No action items could be extracted from this content.
            </div>

            <!-- Items list -->
            <template v-else>
              <div class="viq-actions-modal__toolbar">
                <span>{{ selectedCount }} of {{ items.length }} selected</span>
                <button class="viq-actions-modal__select-all" @click="toggleSelectAll">
                  {{ selectedCount === items.length ? 'Deselect all' : 'Select all' }}
                </button>
              </div>

              <div class="viq-actions-modal__items">
                <label
                  v-for="(item, i) in items"
                  :key="i"
                  class="viq-actions-modal__item"
                  :class="{ 'viq-actions-modal__item--selected': selected[i] }"
                >
                  <input
                    type="checkbox"
                    :checked="selected[i]"
                    class="viq-actions-modal__checkbox"
                    @change="toggleItem(i)"
                  />
                  <div class="viq-actions-modal__item-content">
                    <div class="viq-actions-modal__item-header">
                      <span class="viq-actions-modal__item-title">{{ item.title }}</span>
                      <span
                        class="viq-actions-modal__priority"
                        :class="'viq-actions-modal__priority--' + item.priority"
                      >{{ item.priority }}</span>
                    </div>
                    <p v-if="item.description" class="viq-actions-modal__item-desc">{{ item.description }}</p>
                    <div class="viq-actions-modal__item-meta">
                      <span v-if="item.suggested_deadline" class="viq-actions-modal__deadline">
                        <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                          <rect x="1" y="2" width="8" height="7" rx="1" stroke="currentColor" stroke-width="0.8" />
                          <path d="M3 1v2M7 1v2M1 5h8" stroke="currentColor" stroke-width="0.8" stroke-linecap="round" />
                        </svg>
                        {{ item.suggested_deadline }}
                      </span>
                      <span
                        v-for="tag in (item.tags || []).slice(0, 3)"
                        :key="tag"
                        class="viq-actions-modal__tag"
                      >{{ tag }}</span>
                    </div>
                  </div>
                </label>
              </div>
            </template>
          </div>

          <div v-if="!extracting && !done && items.length > 0" class="viq-actions-modal__footer">
            <button class="viq-actions-modal__cancel" @click="closeModal" :disabled="saving">
              Cancel
            </button>
            <button
              class="viq-actions-modal__send"
              :disabled="saving || selectedCount === 0"
              @click="sendToWorkspace"
            >
              <div v-if="saving" class="viq-actions-modal__spinner viq-actions-modal__spinner--small" />
              <template v-if="saving">Sending...</template>
              <template v-else>
                Send {{ selectedCount }} to Workspace
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M6.5 2l4 4-4 4M10 6H2" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </template>
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: "ViqActionChips",
  props: {
    message: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      modalOpen: false,
      extracting: false,
      items: [],
      selected: {},
      saving: false,
      done: false,
      modalError: null,
    };
  },
  computed: {
    selectedCount() {
      return Object.values(this.selected).filter(Boolean).length;
    },
    actOnEnabled() {
      return !!(this.$config.features && this.$config.features.viqActOn);
    },
    shareEnabled() {
      return !!(this.$config.features && this.$config.features.viqShare);
    },
  },
  methods: {
    async handleActOnThis() {
      this.modalOpen = true;
      this.done = false;
      this.modalError = null;
      this.items = [];
      this.selected = {};
      this.extracting = true;

      try {
        const result = await this.$store.dispatch("Viq/extractActions", {
          content: this.message.content,
          source_type: "qa_answer",
        });

        const extracted = (result && result.items) || [];
        this.items = extracted;
        const sel = {};
        extracted.forEach((_, i) => { sel[i] = true; });
        this.selected = sel;
      } catch {
        this.modalError = "Failed to extract action items.";
      } finally {
        this.extracting = false;
      }
    },

    handleSaveShare() {
      this.$store.dispatch("Viq/openShareDialog", {
        type: "message",
        id: this.message.id,
        content: this.message.content,
      });
    },

    toggleItem(i) {
      this.$set(this.selected, i, !this.selected[i]);
    },

    toggleSelectAll() {
      const allSelected = this.selectedCount === this.items.length;
      const sel = {};
      this.items.forEach((_, i) => { sel[i] = !allSelected; });
      this.selected = sel;
    },

    async sendToWorkspace() {
      this.saving = true;
      this.modalError = null;

      try {
        const selectedItems = this.items.filter((_, i) => this.selected[i]);
        for (const item of selectedItems) {
          await this.$store.dispatch("Viq/createActionItems", {
            title: item.title,
            description: item.description,
            source_type: "qa_answer",
            source_ref: this.message.id,
            suggested_deadline: item.suggested_deadline,
            priority: item.priority,
            tags: item.tags,
          });
        }

        this.done = true;
        setTimeout(() => {
          this.modalOpen = false;
        }, 1500);
      } catch {
        this.modalError = "Failed to send action items.";
      } finally {
        this.saving = false;
      }
    },

    closeModal() {
      if (!this.saving) {
        this.modalOpen = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/styles/viq.scss";

.viq-actions {
  margin-top: $viq-space-md;
  padding-top: $viq-space-sm;
  border-top: 1px solid $viq-border;
  display: flex;
  gap: $viq-space-sm;

  &__btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: $viq-font-sm;
    font-weight: 600;
    color: $viq-accent;
    padding: $viq-space-sm $viq-space-md;
    border: 1px solid $viq-border;
    border-radius: $viq-radius-md;
    background: none;
    cursor: pointer;
    transition: border-color $viq-transition-fast, background $viq-transition-fast;
    white-space: nowrap;

    &:hover {
      border-color: $viq-accent;
      background: $viq-citation-bg;
    }
  }
}

// ── Modal ────────────────────────────────────────────────

.viq-actions-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.4);

  &__panel {
    background: $viq-bg-primary;
    border-radius: $viq-radius-lg;
    box-shadow: $viq-shadow-lg;
    width: 100%;
    max-width: 520px;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
    margin: $viq-space-md;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: $viq-space-md $viq-space-lg;
    border-bottom: 1px solid $viq-border;
  }

  &__header-left {
    display: flex;
    align-items: center;
    gap: $viq-space-sm;
  }

  &__icon {
    width: 32px;
    height: 32px;
    border-radius: $viq-radius-md;
    background: rgba($viq-accent, 0.1);
    color: $viq-accent;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__title {
    font-size: $viq-font-base;
    font-weight: 700;
    color: $viq-text-primary;
    margin: 0;
  }

  &__subtitle {
    font-size: $viq-font-xs;
    color: $viq-text-muted;
    margin: 0;
  }

  &__close {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border: none;
    background: none;
    color: $viq-text-secondary;
    cursor: pointer;
    border-radius: $viq-radius-sm;

    &:hover {
      background: $viq-bg-secondary;
    }
  }

  &__body {
    flex: 1;
    overflow-y: auto;
    padding: $viq-space-lg;
  }

  &__loading {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: $viq-space-sm;
    padding: $viq-space-xl 0;
    color: $viq-text-secondary;
    font-size: $viq-font-sm;
  }

  &__spinner {
    width: 16px;
    height: 16px;
    border: 2px solid $viq-border;
    border-top-color: $viq-accent;
    border-radius: 50%;
    animation: viq-spin 0.8s linear infinite;

    &--small {
      width: 12px;
      height: 12px;
      border-width: 1.5px;
    }
  }

  &__error {
    font-size: $viq-font-sm;
    color: #dc2626;
    background: #fef2f2;
    border-radius: $viq-radius-md;
    padding: $viq-space-sm $viq-space-md;
  }

  &__done {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: $viq-space-sm;
    padding: $viq-space-xl 0;
    color: #15803d;
    font-size: $viq-font-base;
    font-weight: 600;
  }

  &__empty {
    text-align: center;
    padding: $viq-space-xl 0;
    color: $viq-text-secondary;
    font-size: $viq-font-sm;
  }

  &__toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: $viq-space-sm;
    font-size: $viq-font-xs;
    color: $viq-text-secondary;
  }

  &__select-all {
    border: none;
    background: none;
    color: $viq-accent;
    font-size: $viq-font-xs;
    font-weight: 600;
    cursor: pointer;
    padding: 0;

    &:hover {
      text-decoration: underline;
    }
  }

  &__items {
    display: flex;
    flex-direction: column;
    gap: $viq-space-sm;
  }

  &__item {
    display: flex;
    align-items: flex-start;
    gap: $viq-space-sm;
    padding: $viq-space-md;
    border: 1px solid $viq-border;
    border-radius: $viq-radius-md;
    cursor: pointer;
    transition: border-color $viq-transition-fast, background $viq-transition-fast;

    &:hover {
      border-color: darken($viq-border, 10%);
    }

    &--selected {
      border-color: rgba($viq-accent, 0.3);
      background: rgba($viq-accent, 0.03);
    }
  }

  &__checkbox {
    margin-top: 2px;
    accent-color: $viq-accent;
  }

  &__item-content {
    flex: 1;
    min-width: 0;
  }

  &__item-header {
    display: flex;
    align-items: center;
    gap: $viq-space-sm;
    margin-bottom: 2px;
  }

  &__item-title {
    font-size: $viq-font-sm;
    font-weight: 600;
    color: $viq-text-primary;
    line-height: 1.4;
  }

  &__priority {
    font-size: 1rem;
    padding: 1px 6px;
    border-radius: 10px;
    border: 1px solid;
    font-weight: 600;
    text-transform: capitalize;
    white-space: nowrap;

    &--high {
      background: #fef2f2;
      color: #dc2626;
      border-color: #fecaca;
    }

    &--medium {
      background: #fffbeb;
      color: #d97706;
      border-color: #fde68a;
    }

    &--low {
      background: #f9fafb;
      color: #6b7280;
      border-color: #e5e7eb;
    }
  }

  &__item-desc {
    font-size: $viq-font-xs;
    color: $viq-text-secondary;
    line-height: 1.5;
    margin: 0;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  &__item-meta {
    display: flex;
    align-items: center;
    gap: $viq-space-sm;
    margin-top: $viq-space-xs;
  }

  &__deadline {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    font-size: 1rem;
    color: $viq-text-muted;
  }

  &__tag {
    font-size: 1rem;
    background: $viq-bg-secondary;
    color: $viq-text-secondary;
    padding: 1px 6px;
    border-radius: $viq-radius-sm;
  }

  &__footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: $viq-space-sm;
    padding: $viq-space-md $viq-space-lg;
    border-top: 1px solid $viq-border;
  }

  &__cancel {
    border: none;
    background: none;
    color: $viq-text-secondary;
    font-size: $viq-font-xs;
    cursor: pointer;
    padding: $viq-space-sm $viq-space-md;
    border-radius: $viq-radius-md;

    &:hover {
      color: $viq-text-primary;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  &__send {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: $viq-font-xs;
    font-weight: 600;
    color: #fff;
    background: $viq-accent;
    border: none;
    padding: $viq-space-sm $viq-space-md;
    border-radius: $viq-radius-md;
    cursor: pointer;
    transition: background $viq-transition-fast;

    &:hover {
      background: $viq-accent-hover;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}

@keyframes viq-spin {
  to { transform: rotate(360deg); }
}

.viq-fade-enter-active,
.viq-fade-leave-active {
  transition: opacity 0.2s ease;
}
.viq-fade-enter,
.viq-fade-leave-to {
  opacity: 0;
}
</style>
